/**
 * AreaSessionContext — Firebase session + provisional-account bind state for
 * the resumable onboarding area (PR-ONB-RESUME-1B).
 *
 * Mounted app-wide; INERT when the flag is off (no Firebase import, no
 * listeners — the legacy wizard pays zero cost). When on, it tracks the
 * Firebase user via onAuthStateChanged and the per-session bind info
 * (requestPublicId + draftRevision) that the autosave needs.
 *
 * The password never touches this context: pages hand it straight to the
 * Firebase SDK and keep it in local state.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { resumableAreaEnabled } from '@/lib/env';
import { sameBind } from '@/lib/areaGuard';
// Type-only import: erased at build time, so the firebase chunk stays lazy
// (the runtime module is pulled in exclusively via dynamic import below).
import type * as FirebaseClientModule from '@/lib/firebaseClient';
import type { FirebaseUser } from '@/lib/firebaseClient';

export interface AreaBindInfo {
  requestPublicId: string;
  draftRevision: number;
}

export interface AreaSessionValue {
  /** Flag mirror — pages can branch without importing env. */
  enabled: boolean;
  /** False until the first onAuthStateChanged tick resolves. */
  ready: boolean;
  user: FirebaseUser | null;
  /** Bind info for the CURRENT tab session (set after register/login+me). */
  bind: AreaBindInfo | null;
  /** Fresh ID token; force=true bypasses the SDK cache (post email-verify). */
  getToken: (force?: boolean) => Promise<string | null>;
  markBound: (info: AreaBindInfo) => void;
  setDraftRevision: (revision: number) => void;
  /** firebaseUser.reload() — refreshes emailVerified after the user clicks the link. */
  reloadUser: () => Promise<void>;
  signOutArea: () => Promise<void>;
}

const INERT: AreaSessionValue = {
  enabled: false,
  ready: true,
  user: null,
  bind: null,
  getToken: async () => null,
  markBound: () => undefined,
  setDraftRevision: () => undefined,
  reloadUser: async () => undefined,
  signOutArea: async () => undefined,
};

const AreaSessionContext = createContext<AreaSessionValue>(INERT);

export function useAreaSession(): AreaSessionValue {
  return useContext(AreaSessionContext);
}

export function AreaSessionProvider({ children }: { children: ReactNode }): JSX.Element {
  if (!resumableAreaEnabled) {
    return (
      <AreaSessionContext.Provider value={INERT}>
        {children}
      </AreaSessionContext.Provider>
    );
  }
  return <EnabledAreaSessionProvider>{children}</EnabledAreaSessionProvider>;
}

function EnabledAreaSessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [bind, setBind] = useState<AreaBindInfo | null>(null);
  // Lazy-loaded firebase module — single dynamic import for the whole app.
  const firebaseRef = useRef<typeof FirebaseClientModule | null>(null);

  const loadFirebase = useCallback(async () => {
    if (!firebaseRef.current) {
      firebaseRef.current = await import('@/lib/firebaseClient');
    }
    return firebaseRef.current;
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    void loadFirebase().then((fb) => {
      if (cancelled) return;
      unsubscribe = fb.onAuthStateChanged(fb.getFirebaseAuth(), (next) => {
        setUser(next);
        setReady(true);
        if (!next) setBind(null);
      });
    });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [loadFirebase]);

  const getToken = useCallback(
    async (force = false): Promise<string | null> => {
      const fb = await loadFirebase();
      const current = fb.getFirebaseAuth().currentUser;
      if (!current) return null;
      try {
        return await current.getIdToken(force);
      } catch {
        return null;
      }
    },
    [loadFirebase],
  );

  const reloadUser = useCallback(async (): Promise<void> => {
    const fb = await loadFirebase();
    const current = fb.getFirebaseAuth().currentUser;
    if (!current) return;
    await current.reload();
    // onAuthStateChanged does not fire on reload(); re-publish the instance
    // so consumers re-render with the fresh emailVerified.
    setUser(fb.getFirebaseAuth().currentUser);
  }, [loadFirebase]);

  const signOutArea = useCallback(async (): Promise<void> => {
    const fb = await loadFirebase();
    await fb.signOut(fb.getFirebaseAuth());
    setBind(null);
  }, [loadFirebase]);

  // IDEMPOTENT (review fix #1): an identical bind keeps the PREVIOUS object
  // so the context value identity is stable and effects keyed on it don't
  // re-fire (the guard would otherwise loop on GET /area/me).
  const markBound = useCallback((info: AreaBindInfo) => {
    setBind((previous) => (sameBind(previous, info) ? previous : info));
  }, []);
  const setDraftRevision = useCallback(
    (revision: number) =>
      setBind((prev) =>
        prev === null || prev.draftRevision === revision
          ? prev
          : { ...prev, draftRevision: revision },
      ),
    [],
  );

  const value = useMemo<AreaSessionValue>(
    () => ({
      enabled: true,
      ready,
      user,
      bind,
      getToken,
      markBound,
      setDraftRevision,
      reloadUser,
      signOutArea,
    }),
    [ready, user, bind, getToken, markBound, setDraftRevision, reloadUser, signOutArea],
  );

  return (
    <AreaSessionContext.Provider value={value}>
      {children}
    </AreaSessionContext.Provider>
  );
}
